const navigationContainers = document.querySelectorAll('.in-page-navigation');

navigationContainers.forEach(navigationContainer => {
  const navigationLinks = navigationContainer.querySelectorAll('.in-page-navigation__link');
  const hideLinksWithoutTargets = navigationContainer?.getAttribute('data-hide-links-without-targets') === 'true';
  const addToCartButton = navigationContainer.querySelector('.in-page-navigation__add-to-cart');
  const toggleButton = navigationContainer.querySelector('.in-page-navigation__toggle');
  const linksContainer = navigationContainer.querySelector('.in-page-navigation__links');

  // Function to update toggle button text
  const updateToggleText = () => {
    if (!toggleButton || !navigationLinks.length) return;
    
    const toggleText = toggleButton.querySelector('.in-page-navigation__toggle-active');
    if (!toggleText) return;
    
    // Find the active link
    const activeLink = Array.from(navigationLinks).find(link => 
      link.classList.contains('in-page-navigation__link--active')
    );
    
    // Use active link text, or first visible link text as fallback
    const visibleLinks = Array.from(navigationLinks).filter(link => link.style.display !== 'none');
    const textToShow = activeLink ? activeLink.textContent : (visibleLinks[0]?.textContent || 'Link');
    toggleText.textContent = textToShow;
  };

  // Handle mobile toggle
  if (toggleButton && linksContainer) {
    // Set initial toggle text
    updateToggleText();
    
    toggleButton.addEventListener('click', function(e) {
      e.preventDefault();
      const isExpanded = linksContainer.classList.contains('in-page-navigation__links--expanded');
      
      if (isExpanded) {
        linksContainer.classList.remove('in-page-navigation__links--expanded');
        toggleButton.setAttribute('aria-expanded', 'false');
      } else {
        linksContainer.classList.add('in-page-navigation__links--expanded');
        toggleButton.setAttribute('aria-expanded', 'true');
      }
    });

    // Close dropdown when clicking outside
    document.addEventListener('click', function(e) {
      if (!navigationContainer.contains(e.target)) {
        linksContainer.classList.remove('in-page-navigation__links--expanded');
        toggleButton.setAttribute('aria-expanded', 'false');
      }
    });

    // Close dropdown when scrolling
    let scrollTimeout;
    window.addEventListener('scroll', function() {
      // Only close if dropdown is open
      if (linksContainer.classList.contains('in-page-navigation__links--expanded')) {
        // Debounce to avoid closing immediately on programmatic scroll from link clicks
        clearTimeout(scrollTimeout);
        scrollTimeout = setTimeout(() => {
          linksContainer.classList.remove('in-page-navigation__links--expanded');
          toggleButton.setAttribute('aria-expanded', 'false');
        }, 50);
      }
    }, { passive: true });
  }

  // Flag to prevent observer from clearing active state during click navigation
  let isNavigating = false;

  if (navigationLinks) {
    navigationLinks.forEach(link => {
      // Check if link is a fragment identifier and target exists (only if setting is enabled)
      if (hideLinksWithoutTargets) {
        const href = link.getAttribute('href');
        if (href && href.startsWith('#')) {
          const targetId = href.substring(1);
          const targetElement = document.getElementById(targetId);
          
          // Hide link if target doesn't exist
          if (!targetElement && href !== '#bv_review_maincontainer') {
            link.style.display = 'none';
            return; // Skip adding click listener for hidden links
          }
        }
      }
      
      link.addEventListener('click', function(e) {
        // Close mobile dropdown when a link is clicked
        if (toggleButton && linksContainer) {
          linksContainer.classList.remove('in-page-navigation__links--expanded');
          toggleButton.setAttribute('aria-expanded', 'false');
        }
        
        // Set navigating flag to preserve active state during scroll
        isNavigating = true;
        
        // Remove active class from all links
        navigationLinks.forEach(otherLink => {
          otherLink.classList.remove('in-page-navigation__link--active');
        });
        
        // Add active class to clicked link
        this.classList.add('in-page-navigation__link--active');
        
        // Update toggle text
        updateToggleText();
      });
    });

    // Set up intersection observer for scroll-based active link detection
    // Calculate the height of the sticky navigation to offset the trigger point
    const navHeight = navigationContainer.offsetHeight;
    const observerOptions = {
      root: null,
      rootMargin: `-${navHeight + 1}px 0px -99% 0px`, 
      threshold: 0
    };

    // Track currently visible sections
    const visibleSections = new Set();

    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        const targetId = entry.target.id;

        if (entry.isIntersecting) {
          // Clear navigating flag when target section becomes visible
          isNavigating = false;
          
          // Add to visible sections
          visibleSections.add(targetId);

          // Find the corresponding link for this target element
          const correspondingLink = Array.from(navigationLinks).find(link => {
            const href = link.getAttribute('href');
            return href && href === `#${targetId}`;
          });

          if (correspondingLink) {
            // Remove active class from all links
            navigationLinks.forEach(otherLink => {
              otherLink.classList.remove('in-page-navigation__link--active');
            });
            
            // Add active class to the corresponding link
            correspondingLink.classList.add('in-page-navigation__link--active');
            
            // Update toggle text
            updateToggleText();
          }
        } else {
          // Remove from visible sections
          visibleSections.delete(targetId);

          // If no sections are visible and not navigating, remove all active states
          if (visibleSections.size === 0 && !isNavigating) {
            navigationLinks.forEach(link => {
              link.classList.remove('in-page-navigation__link--active');
            });
            
            // Update toggle text
            updateToggleText();
          }
        }
      });
    }, observerOptions);

    // Observe all target elements
    navigationLinks.forEach(link => {
      const href = link.getAttribute('href');
      if (href && href.startsWith('#')) {
        const targetId = href.substring(1);
        const targetElement = document.getElementById(targetId);
        
        if (targetElement) {
          observer.observe(targetElement);
        }
      }
    });
  }

  // Set up sticky detection for in-page navigation sections
  const stickyNavSection = navigationContainer.closest('.section--in-page-navigation');
  if (stickyNavSection) {
    // Create a sentinel element to detect when the section becomes stuck
    // Must be in document flow (not absolute) to track scroll position correctly
    const sentinel = document.createElement('div');
    sentinel.className = 'in-page-navigation-sentinel';
    sentinel.style.cssText = 'height: 1px; margin-bottom: -1px; pointer-events: none; visibility: hidden;';
    stickyNavSection.parentNode.insertBefore(sentinel, stickyNavSection);

    const stickyObserver = new IntersectionObserver(
      ([entry]) => {
        // When sentinel is not visible (scrolled past), the nav is stuck
        stickyNavSection.classList.toggle('is-stuck', !entry.isIntersecting);
      },
      { threshold: 0 }
    );

    stickyObserver.observe(sentinel);
  }

  // Handle add to cart button click
  if (addToCartButton) {
    addToCartButton.addEventListener('click', async function(e) {
      e.preventDefault();
      
      // Get data attributes
      const addToCartSectionId = navigationContainer.getAttribute('data-add-to-cart-section-id');
      const variantId = navigationContainer.getAttribute('data-variant-id');
      const productHandle = navigationContainer.getAttribute('data-product-handle');
      const productUrl = navigationContainer.getAttribute('data-product-url');
      const currentProductHandle = navigationContainer.getAttribute('data-current-product-handle');
      const hasOnlyDefaultVariant = navigationContainer.getAttribute('data-product-has-only-default-variant') === 'true';
      
      // Check if we're on the product page by comparing handles
      const isOnProductPage = currentProductHandle && currentProductHandle === productHandle;

      // Product has no variants
      if (hasOnlyDefaultVariant) {
        await addProductToCart(variantId);
        return;
      }
      
      // Product has variants AND on product page → Navigate to section
      if (isOnProductPage && addToCartSectionId) {
        const sectionElement = document.querySelector(addToCartSectionId);
        if (sectionElement) {
          sectionElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
        return;
      }
      
      // Product has variants AND not on product page → Navigate to product URL
      if (!isOnProductPage) {
        window.location.href = productUrl;
        return;
      }
    });
  }
});

// Helper function to add product to cart
async function addProductToCart(variantId) {    
  // Create FormData
  const formData = new FormData();
  formData.append('id', variantId);
  formData.append('quantity', 1);
  formData.append('attributes[recent_id]', variantId);
    
  // Add to cart
  const response = await fetch('/cart/add.js', {
    method: 'POST',
    body: formData
  });

  if (!response.ok) {
    const errorData = await response.json();
    throw new Error(errorData.description || 'Failed to add to cart');
  }

  // Trigger cart modal or update cart
  const addToCartEvent = new CustomEvent('cart:item-added');
  document.dispatchEvent(addToCartEvent);
}
